import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Empty,
  Input,
  InputNumber,
  Modal,
  Space,
  Spin,
  Switch,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  FileTextOutlined,
  LockOutlined,
  MessageOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  SendOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import message from "@/utils/globalMessage";
import {
  SERVICE_REQUEST_STATUS_COLORS,
  SERVICE_REQUEST_STATUS_LABELS,
  SERVICE_REQUEST_STEPS,
} from "./constants";
import {
  ActionBar,
  ActionGroup,
  AmountValue,
  BackButton,
  BillableBadge,
  BillableContent,
  BillableHeader,
  BillableSection,
  BillableStatus,
  BillableToggleRow,
  CardGrid,
  DetailCard,
  DetailField,
  DetailFieldGrid,
  DetailLabel,
  DetailValue,
  DescriptionValue,
  EmptyNotes,
  Header,
  HeaderCopy,
  HeaderIdentity,
  HeaderTitle,
  Page,
  QuoteControls,
  QuoteHint,
  QuoteInput,
  RequestId,
  SectionHeading,
  StatusPill,
  StepConnector,
  StepDot,
  StepItem,
  StepLabel,
  Stepper,
  StepTrack,
  Timeline,
  TimelineItem,
  TimelineMeta,
  TimelineText,
  TimelineTime,
  TimelineAuthor,
  WorkOrderLink,
} from "./ServiceRequestDetail.styles";

const FALLBACK_STATUS_LABELS = {
  submitted: "Submitted",
  reviewing: "Reviewing",
  quote_sent: "Quote Sent",
  quote_approved: "Quote Approved",
  quote_rejected: "Quote Rejected",
  in_progress: "In Progress",
  resolved: "Resolved",
  closed: "Closed",
  open: "Submitted",
  processing: "Reviewing",
  converted: "In Progress",
};

const FALLBACK_STEPS = [
  { key: "submitted", label: "Submitted" },
  { key: "reviewing", label: "Reviewing" },
  { key: "quote_sent", label: "Quote Sent", billableOnly: true },
  { key: "quote_approved", label: "Approved", billableOnly: true },
  { key: "in_progress", label: "In Progress" },
  { key: "resolved", label: "Resolved" },
  { key: "closed", label: "Closed" },
];

const STEP_ICONS = {
  submitted: <FileTextOutlined />,
  reviewing: <SearchOutlined />,
  quote_sent: <DollarOutlined />,
  quote_approved: <CheckCircleOutlined />,
  quote_rejected: <CloseCircleOutlined />,
  in_progress: <ToolOutlined />,
  resolved: <CheckOutlined />,
  closed: <LockOutlined />,
};

const STATUS_TONE = {
  submitted: "submitted",
  reviewing: "reviewing",
  quote_sent: "quote_sent",
  quote_approved: "quote_approved",
  quote_rejected: "quote_rejected",
  in_progress: "in_progress",
  resolved: "resolved",
  closed: "closed",
  open: "submitted",
  processing: "reviewing",
  converted: "in_progress",
};

const COST_STATUS_LABELS = {
  not_required: "Not required",
  pending: "Awaiting tenant approval",
  approved: "Approved",
  rejected: "Quote rejected",
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.status ||
  error?.response?.data?.detail ||
  error?.response?.data?.quoted_cost ||
  error?.response?.data?.decision ||
  fallback;

const formatDate = (value) =>
  value ? moment(value).format("YYYY-MM-DD HH:mm") : "-";

const getLabel = (status) =>
  SERVICE_REQUEST_STATUS_LABELS?.[status] || FALLBACK_STATUS_LABELS[status] || status || "-";

const getTone = (status) =>
  STATUS_TONE[status] || (SERVICE_REQUEST_STATUS_COLORS?.[status] ? status : "closed");

const getInitials = (name) => {
  const value = String(name || "?").trim();
  if (!value) return "?";
  const parts = value.split(/\s+/).filter(Boolean);
  return (parts.length > 1 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : value.slice(0, 2))
    .toUpperCase();
};

const getRecordBillable = (record) => {
  if (typeof record?.is_billable === "boolean") return record.is_billable;
  if (typeof record?.billable === "boolean") return record.billable;
  return record?.quoted_cost !== null && record?.quoted_cost !== undefined;
};

const getStepDefinitions = (billable) => {
  const source = Array.isArray(SERVICE_REQUEST_STEPS) && SERVICE_REQUEST_STEPS.length
    ? SERVICE_REQUEST_STEPS
    : FALLBACK_STEPS;
  return source.filter((step) => {
    if (step.key === "quote_rejected") return false;
    return billable || !step.billableOnly;
  });
};

const getProgressStatus = (status) => {
  if (status === "open") return "submitted";
  if (status === "processing") return "reviewing";
  if (status === "converted") return "in_progress";
  if (status === "quote_rejected") return "quote_sent";
  return status;
};

const StepperView = ({ status, billable }) => {
  const steps = getStepDefinitions(billable);
  const progressStatus = getProgressStatus(status);
  const currentIndex = Math.max(0, steps.findIndex((step) => step.key === progressStatus));
  const rejectedIndex = status === "quote_rejected"
    ? steps.findIndex((step) => step.key === "quote_sent")
    : -1;

  return (
    <Stepper aria-label="Service request progress">
      <StepTrack>
        {steps.map((step, index) => {
          const rejected = index === rejectedIndex;
          const done = !rejected && index < currentIndex;
          const active = !rejected && index === currentIndex;
          return (
            <StepItem key={step.key} $active={active} $done={done} $rejected={rejected}>
              <StepDot $active={active} $done={done} $rejected={rejected}>
                {done ? <CheckOutlined /> : rejected ? <CloseCircleOutlined /> : STEP_ICONS[step.key]}
              </StepDot>
              <StepLabel $active={active} $done={done} $rejected={rejected}>
                {step.key === "quote_approved" ? "Approved" : step.label}
              </StepLabel>
              {index < steps.length - 1 ? (
                <StepConnector $done={index < currentIndex && !rejected} $active={active} />
              ) : null}
            </StepItem>
          );
        })}
      </StepTrack>
    </Stepper>
  );
};

export default function ServiceRequestDetail({
  requestId,
  listPath,
  loadRequest,
  addNote,
  canAddNote,
  closeRequest,
  reopenRequest,
  canManageCost = false,
  updateBillable,
  startReview,
  quoteCost,
  canCreateWorkorder = false,
  createWorkorder,
  getRelatedWorkorderPath,
  respondCost,
  role,
}) {
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [submittingNote, setSubmittingNote] = useState(false);
  const [quotedCost, setQuotedCost] = useState(null);
  const [submittingQuote, setSubmittingQuote] = useState(false);
  const [updatingBillable, setUpdatingBillable] = useState(false);
  const [startingReview, setStartingReview] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [respondingCost, setRespondingCost] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectComment, setRejectComment] = useState("");

  const loadRecord = useCallback(async () => {
    setLoading(true);
    try {
      const response = await loadRequest(requestId);
      setRecord(response?.data ?? response);
    } catch (error) {
      message.error(getErrorMessage(error, "Failed to load service request"));
    } finally {
      setLoading(false);
    }
  }, [loadRequest, requestId]);

  useEffect(() => {
    loadRecord();
  }, [loadRecord]);

  useEffect(() => {
    setQuotedCost(
      record?.quoted_cost === null || record?.quoted_cost === undefined
        ? null
        : Number(record.quoted_cost)
    );
  }, [record?.id, record?.quoted_cost]);

  const isBillable = getRecordBillable(record);
  const isFacilityRole = role === "facility" || role === "portfolio" || (
    role !== "tenant" && Boolean(canManageCost || updateBillable || startReview || canCreateWorkorder)
  );
  const isTenantRole = role === "tenant" || !isFacilityRole;
  const currentStatus = record?.status || "submitted";
  const noteEnabled = Boolean(canAddNote && record && currentStatus !== "closed");
  const canEditBillable = Boolean(
    isFacilityRole && canManageCost && updateBillable && record &&
      !["closed", "in_progress", "resolved"].includes(currentStatus) && !record.has_workorder
  );
  const canQuote = Boolean(
    isFacilityRole && canManageCost && quoteCost && record && isBillable &&
      !["closed", "in_progress", "resolved"].includes(currentStatus) && !record.has_workorder
  );
  const canStartReview = Boolean(
    isFacilityRole && startReview && record && currentStatus === "submitted" && !record.has_workorder
  );
  const canConvert = Boolean(
    isFacilityRole && canCreateWorkorder && createWorkorder && record && !record.has_workorder &&
      !["closed", "in_progress", "resolved"].includes(currentStatus) &&
      (
        (!isBillable && currentStatus === "reviewing" && record.cost_approval_status === "not_required") ||
        (isBillable && currentStatus === "quote_approved" && record.cost_approval_status === "approved")
      )
  );
  const canReopen = Boolean(
    record && reopenRequest && (
      (isTenantRole && currentStatus === "closed") ||
      (isFacilityRole && ["resolved", "closed"].includes(currentStatus))
    )
  );
  const canConfirmClose = Boolean(isTenantRole && record && currentStatus === "resolved" && closeRequest);
  const hasPendingQuote = Boolean(
    isTenantRole && respondCost && isBillable && record &&
      Number(record.quoted_cost) > 0 &&
      (record.cost_approval_status === "pending" || currentStatus === "quote_sent")
  );

  const notes = useMemo(
    () => [...(record?.notes || [])].sort((a, b) => new Date(b.created_date) - new Date(a.created_date)),
    [record?.notes]
  );

  const submitNote = async () => {
    const content = note.trim();
    if (!content || !addNote) {
      message.error("Please enter a note");
      return;
    }
    setSubmittingNote(true);
    try {
      await addNote(requestId, content);
      setNote("");
      setNoteModalOpen(false);
      await loadRecord();
      message.success("Note added successfully");
    } catch (error) {
      message.error(getErrorMessage(error, "Failed to add note"));
    } finally {
      setSubmittingNote(false);
    }
  };

  const toggleBillable = async (checked) => {
    if (!updateBillable || !record) return;
    setUpdatingBillable(true);
    try {
      await updateBillable(record.id, checked);
      await loadRecord();
      message.success(checked ? "Request marked as billable" : "Billable cost removed");
    } catch (error) {
      message.error(getErrorMessage(error, "Failed to update billable setting"));
    } finally {
      setUpdatingBillable(false);
    }
  };

  const sendQuote = async () => {
    const amount = Number(quotedCost);
    if (!quoteCost || !Number.isFinite(amount) || amount <= 0) {
      message.error("Enter a valid USD amount greater than zero");
      return;
    }
    setSubmittingQuote(true);
    try {
      await quoteCost(record.id, amount);
      await loadRecord();
      message.success("Cost quote sent to tenant");
    } catch (error) {
      message.error(getErrorMessage(error, "Failed to send cost quote"));
    } finally {
      setSubmittingQuote(false);
    }
  };

  const startReviewRequest = async () => {
    if (!startReview || !record) return;
    setStartingReview(true);
    try {
      await startReview(record.id);
      await loadRecord();
      message.success("Request moved to reviewing");
    } catch (error) {
      message.error(getErrorMessage(error, "Failed to start review"));
    } finally {
      setStartingReview(false);
    }
  };

  const changeRequestStatus = async (action, successText) => {
    if (!action || !record) return;
    setChangingStatus(true);
    try {
      await action(record.id);
      await loadRecord();
      message.success(successText);
    } catch (error) {
      message.error(getErrorMessage(error, "Failed to update request status"));
    } finally {
      setChangingStatus(false);
    }
  };

  const respondToCost = async (decision, comment) => {
    if (!respondCost || !record) return false;
    setRespondingCost(true);
    try {
      await respondCost(record.id, decision, comment?.trim() || undefined);
      await loadRecord();
      message.success(decision === "approved" ? "Cost quote approved" : "Cost quote rejected");
      return true;
    } catch (error) {
      message.error(getErrorMessage(error, "Failed to respond to cost quote"));
      return false;
    } finally {
      setRespondingCost(false);
    }
  };

  const rejectQuote = async () => {
    const success = await respondToCost("rejected", rejectComment);
    if (success) {
      setRejectComment("");
      setRejectModalOpen(false);
    }
  };

  const billableAmount = record?.quoted_cost === null || record?.quoted_cost === undefined
    ? null
    : Number(record.quoted_cost);
  const costStatusLabel = COST_STATUS_LABELS[record?.cost_approval_status] || "Not required";

  const actionButtons = (
    <ActionGroup>
      {noteEnabled ? (
        <Button icon={<MessageOutlined />} onClick={() => setNoteModalOpen(true)}>
          Add Note
        </Button>
      ) : null}
      {canStartReview ? (
        <Button type="primary" icon={<PlayCircleOutlined />} onClick={startReviewRequest} loading={startingReview}>
          Start Review
        </Button>
      ) : null}
      {canConvert ? (
        <Button type="primary" icon={<ToolOutlined />} onClick={() => createWorkorder(record)}>
          Create Work Order
        </Button>
      ) : null}
      {canConfirmClose ? (
        <Button
          type="primary"
          icon={<LockOutlined />}
          onClick={() => changeRequestStatus(closeRequest, "Request closed")}
          loading={changingStatus}
        >
          Confirm &amp; Close
        </Button>
      ) : null}
      {canReopen ? (
        <Button
          icon={<ReloadOutlined />}
          onClick={() => changeRequestStatus(reopenRequest, "Request reopened")}
          loading={changingStatus}
        >
          Reopen
        </Button>
      ) : null}
    </ActionGroup>
  );

  return (
    <Page>
      <Header>
        <HeaderIdentity>
          <BackButton icon={<ArrowLeftOutlined />} onClick={() => navigate(listPath)} aria-label="Back" />
          <HeaderCopy>
            <HeaderTitle level={4}>Service Request {record ? `#${record.id}` : ""}</HeaderTitle>
            {record ? <RequestId>{record.title}</RequestId> : null}
          </HeaderCopy>
          {record ? (
            <StatusPill $status={getTone(currentStatus)}>{getLabel(currentStatus)}</StatusPill>
          ) : null}
        </HeaderIdentity>
      </Header>

      <Spin spinning={loading}>
        {record ? (
          <>
            <StepperView status={currentStatus} billable={isBillable} />
            <ActionBar>{actionButtons}</ActionBar>

            {(isFacilityRole || isBillable) ? <BillableSection>
              <BillableHeader>
                <SectionHeading><DollarOutlined /> Billing &amp; Cost</SectionHeading>
                <BillableBadge $enabled={isBillable}>{isBillable ? "Billable" : "No charge"}</BillableBadge>
              </BillableHeader>
              <BillableContent>
                {isFacilityRole ? (
                  <>
                    <BillableToggleRow>
                      <Switch
                        checked={isBillable}
                        onChange={toggleBillable}
                        loading={updatingBillable}
                        disabled={!canEditBillable || updatingBillable}
                      />
                      <Typography.Text strong>{isBillable ? "This request is billable" : "Mark as billable"}</Typography.Text>
                    </BillableToggleRow>
                    {isBillable ? (
                      <QuoteControls>
                        <QuoteInput>
                          <InputNumber
                            min={0}
                            precision={2}
                            value={quotedCost}
                            onChange={setQuotedCost}
                            placeholder={billableAmount == null ? "0.00" : billableAmount.toFixed(2)}
                            addonBefore="USD"
                            disabled={!canQuote || submittingQuote}
                          />
                        </QuoteInput>
                        <Button
                          type="primary"
                          icon={<SendOutlined />}
                          onClick={sendQuote}
                          loading={submittingQuote}
                          disabled={!canQuote || !Number.isFinite(Number(quotedCost)) || Number(quotedCost) <= 0}
                        >
                          Send Quote to Tenant
                        </Button>
                        <QuoteHint>{record.cost_approval_status === "pending" ? "Tenant approval is pending" : "Enter a cost to notify the tenant"}</QuoteHint>
                      </QuoteControls>
                    ) : null}
                  </>
                ) : (
                  <>
                    {isBillable && billableAmount !== null ? (
                      <Space size={28} wrap>
                        <div>
                          <DetailLabel>Quoted Cost</DetailLabel>
                          <AmountValue>${billableAmount.toFixed(2)}</AmountValue>
                        </div>
                        <div>
                          <DetailLabel>Approval Status</DetailLabel>
                          <BillableStatus $status={record.cost_approval_status}>
                            {costStatusLabel}
                          </BillableStatus>
                        </div>
                      </Space>
                    ) : (
                      <Typography.Text type="secondary">No cost quote has been sent.</Typography.Text>
                    )}
                    {hasPendingQuote ? (
                      <Space wrap>
                        <Button
                          type="primary"
                          icon={<CheckOutlined />}
                          onClick={() => respondToCost("approved")}
                          loading={respondingCost}
                        >
                          Approve Quote
                        </Button>
                        <Button
                          danger
                          icon={<CloseCircleOutlined />}
                          onClick={() => setRejectModalOpen(true)}
                          loading={respondingCost}
                        >
                          Reject Quote
                        </Button>
                      </Space>
                    ) : null}
                  </>
                )}
              </BillableContent>
            </BillableSection> : null}

            <CardGrid>
              <DetailCard>
                <SectionHeading><FileTextOutlined /> Request Information</SectionHeading>
                <DetailField>
                  <DetailLabel>Title / Issue</DetailLabel>
                  <DetailValue>{record.title || "-"}</DetailValue>
                </DetailField>
                <DetailFieldGrid>
                  <DetailField>
                    <DetailLabel>Facility</DetailLabel>
                    <DetailValue>{record.facility_name || record.facility || "-"}</DetailValue>
                  </DetailField>
                  <DetailField>
                    <DetailLabel>Suite / Area</DetailLabel>
                    <DetailValue>{record.suite_area || "-"}</DetailValue>
                  </DetailField>
                  <DetailField>
                    <DetailLabel>Category</DetailLabel>
                    <DetailValue>{record.category_name || record.category || "-"}</DetailValue>
                  </DetailField>
                  <DetailField>
                    <DetailLabel>Priority</DetailLabel>
                    <DetailValue $priority={record.priority}>{record.priority || "-"}</DetailValue>
                  </DetailField>
                </DetailFieldGrid>
              </DetailCard>

              <DetailCard>
                <SectionHeading><MessageOutlined /> Requester &amp; Dates</SectionHeading>
                <DetailField>
                  <DetailLabel>Requested By</DetailLabel>
                  <DetailValue>{record.createdby_name || "-"}</DetailValue>
                </DetailField>
                <DetailField>
                  <DetailLabel>Company</DetailLabel>
                  <DetailValue>{record.company_name || "-"}</DetailValue>
                </DetailField>
                <DetailFieldGrid>
                  <DetailField>
                    <DetailLabel>Submitted</DetailLabel>
                    <DetailValue>{formatDate(record.created_date)}</DetailValue>
                  </DetailField>
                  <DetailField>
                    <DetailLabel>Last Updated</DetailLabel>
                    <DetailValue>{formatDate(record.modified_date || record.created_date)}</DetailValue>
                  </DetailField>
                </DetailFieldGrid>
              </DetailCard>

              <DetailCard $full>
                <SectionHeading><FileTextOutlined /> Description</SectionHeading>
                <DescriptionValue>{record.description || "No description provided."}</DescriptionValue>
              </DetailCard>

              {record.related_workorder ? (
                <DetailCard $full>
                  <SectionHeading><ToolOutlined /> Work Order</SectionHeading>
                  {record.related_workorder.is_deleted ? (
                    <DetailValue>Deleted work order</DetailValue>
                  ) : getRelatedWorkorderPath ? (
                    <WorkOrderLink
                      type="link"
                      onClick={() => navigate(getRelatedWorkorderPath(record.related_workorder, record))}
                    >
                      {record.related_workorder.workordernumber || `Work Order #${record.related_workorder.id}`}
                    </WorkOrderLink>
                  ) : (
                    <DetailValue>{record.related_workorder.workordernumber || `Work Order #${record.related_workorder.id}`}</DetailValue>
                  )}
                </DetailCard>
              ) : null}

              <DetailCard $full>
                <SectionHeading $spread>
                  <span><MessageOutlined /> Activity &amp; Notes</span>
                  {noteEnabled ? (
                    <Button size="small" icon={<PlusOutlined />} onClick={() => setNoteModalOpen(true)}>
                      Add Note
                    </Button>
                  ) : null}
                </SectionHeading>
                {notes.length ? (
                  <Timeline>
                    {notes.map((item) => {
                      const author = item.createdby_name || "Unknown";
                      return (
                        <TimelineItem key={item.id || `${item.created_date}-${author}`}>
                          <Avatar size={36}>{getInitials(author)}</Avatar>
                          <div>
                            <TimelineMeta>
                              <TimelineAuthor>{author}</TimelineAuthor>
                              <TimelineTime>{formatDate(item.created_date)}</TimelineTime>
                            </TimelineMeta>
                            <TimelineText>{item.content || item.text || ""}</TimelineText>
                          </div>
                        </TimelineItem>
                      );
                    })}
                  </Timeline>
                ) : (
                  <EmptyNotes image={Empty.PRESENTED_IMAGE_SIMPLE} description="No notes yet" />
                )}
              </DetailCard>
            </CardGrid>
          </>
        ) : null}
      </Spin>

      <Modal
        title="Add Note"
        open={noteModalOpen}
        onCancel={() => {
          setNoteModalOpen(false);
          setNote("");
        }}
        onOk={submitNote}
        okText="Add Note"
        confirmLoading={submittingNote}
        okButtonProps={{ disabled: !note.trim() }}
        destroyOnClose
      >
        <Input.TextArea
          rows={5}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Enter note"
          maxLength={4000}
          showCount
        />
      </Modal>

      <Modal
        title="Reject Quote"
        open={rejectModalOpen}
        onCancel={() => {
          setRejectModalOpen(false);
          setRejectComment("");
        }}
        onOk={rejectQuote}
        okText="Reject Quote"
        okButtonProps={{ danger: true }}
        confirmLoading={respondingCost}
        destroyOnClose
      >
        <Input.TextArea
          rows={4}
          value={rejectComment}
          onChange={(event) => setRejectComment(event.target.value)}
          placeholder="Comment (optional)"
          maxLength={4000}
          showCount
        />
      </Modal>
    </Page>
  );
}
